import React, { useEffect, useState } from "react";
import { Menu, X, Leaf, Moon, Sun } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../ThemeContext";

const NAV_LINKS = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Opportunities", id: "opportunities" },
  { label: "Future Mobility", id: "future-mobility" },
  { label: "Why Choose Us", id: "why-choose-us" },
  { label: "Contact", id: "contact" },
  { label: "Careers", path: "/careers" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => {
      if (!isHomePage) {
        setScrolled(true);
        return;
      }
      setScrolled(window.scrollY > 20);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, [isHomePage]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileOpen]);

  const scrollToSection = (id) => {
    if (!isHomePage) {
      navigate("/");

      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 400);
    } else {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  };

  const ThemeIcon = isDark ? Sun : Moon;

  return (
    <>
      {/* HEADER */}
      <header
        className={`fixed top-0 left-0 right-0 z-[90] py-3 lg:py-5
        transition-[background-color,backdrop-filter,border-color,transform]
        duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
        ${
          isHomePage && !scrolled
            ? isDark
              ? "bg-black lg:bg-black/20"
              : "bg-white lg:bg-white/20"
            : isDark
            ? "bg-black/95 backdrop-blur-xl border-b border-white/10"
            : "bg-white/95 backdrop-blur-xl border-b border-black/10"
        }`}
      >
        <div className="w-full px-4 md:px-8 xl:px-12 flex items-center justify-between">
          {/* LOGO */}
          <button
            onClick={() => {
              navigate("/");
              setTimeout(() => {
                const el = document.getElementById("home");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 200);
            }}
            className="transition-transform duration-500 hover:scale-[1.03] active:scale-95"
          >
            <img src="/logo.png" className="h-10 md:h-12 xl:h-14" />
          </button>

          {/* DESKTOP NAV */}
          <nav className="hidden lg:flex flex-1 justify-center gap-6">
            {NAV_LINKS.map((item) =>
              item.path ? (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                    isDark
                      ? "text-white/80 hover:text-green-400"
                      : "text-black/80 hover:text-green-600"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                    isDark
                      ? "text-white/80 hover:text-green-400"
                      : "text-black/80 hover:text-green-600"
                  }`}
                >
                  {item.label}
                </button>
              )
            )}
          </nav>

          {/* DESKTOP ACTIONS */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="transition-transform duration-500 hover:rotate-180"
            >
              <ThemeIcon
                className={`w-4 h-4 ${
                  isDark ? "text-white" : "text-black"
                }`}
              />
            </button>

            <Link
              to="/enquiry"
              className="btn-enquire-enhanced transition-all duration-500 hover:-translate-y-1 hover:scale-[1.02]"
            >
              <span className="btn-label flex items-center gap-2">
                <Leaf className="w-4 h-4" />
                Enquire Now
              </span>
            </Link>
          </div>

          {/* MOBILE BUTTONS */}
          <div className="flex items-center gap-3 lg:hidden">
            <button
              onClick={toggleTheme}
              className="transition-transform duration-500 hover:rotate-180"
            >
              <ThemeIcon
                className={`w-5 h-5 ${
                  isDark ? "text-white" : "text-black"
                }`}
              />
            </button>

            <button onClick={() => setMobileOpen(true)}>
              <Menu
                className={`w-7 h-7 transition-transform duration-500 hover:rotate-90 ${
                  isDark ? "text-white" : "text-black"
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE MENU */}
      {mobileOpen && (
        <div
          className={`fixed inset-0 z-[9999] lg:hidden isolate
          transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
          animate-in fade-in slide-in-from-right-8
          ${isDark ? "bg-black" : "bg-white"}`}
        >
          {/* TOP BAR */}
          <div
            className={`flex justify-between items-center px-5 py-5 border-b
            ${isDark ? "border-white/10" : "border-black/10"}`}
          >
            <img
              src="/logo.png"
              className="h-10 transition-transform duration-500 hover:scale-105"
            />

            <button onClick={() => setMobileOpen(false)}>
              <X
                className={`w-7 h-7 transition-transform duration-500 hover:rotate-90 ${
                  isDark ? "text-white" : "text-black"
                }`}
              />
            </button>
          </div>

          {/* LINKS */}
          <div className="flex flex-col gap-6 px-6 py-10">
            {NAV_LINKS.map((item) =>
              item.path ? (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`text-lg transition-all duration-300 hover:translate-x-2 ${
                    isDark ? "text-white" : "text-black"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => {
                    setMobileOpen(false);
                    setTimeout(() => scrollToSection(item.id), 100);
                  }}
                  className={`text-lg text-left transition-all duration-300 hover:translate-x-2 ${
                    isDark ? "text-white" : "text-black"
                  }`}
                >
                  {item.label}
                </button>
              )
            )}

            {/* MOBILE CTA */}
            <Link
              to="/enquiry"
              onClick={() => setMobileOpen(false)}
              className="btn-enquire-enhanced mt-6 transition-all duration-500 hover:-translate-y-1 hover:scale-[1.02]"
            >
              <span className="btn-label flex items-center justify-center gap-2">
                <Leaf className="w-4 h-4" />
                Enquire Now
              </span>
            </Link>
          </div>
        </div>
      )}
    </>
  );
}